import { Injectable } from '@angular/core';
import * as GC from '@grapecity/spread-sheets';
import { isBaselineDateNewerThanStress, isDatasetNewer, isDatasetStillInDB } from '@helpers/outofdateResultsetRules';
import { DatasetDetails } from '@models/dataset';
import { ResultItem, ResultsetItem } from '@models/resultset-item';
import { Scenario } from '@models/scenario';
import { DatasetsService } from '@services/datasets/datasets.service';
import { NotificationsService } from '@services/notifications/notifications.service';
import { ResultsService } from '@services/results/results.service';
import { ScenarioAction, ScenarioService } from '@services/scenario/scenario.service';
import { SettingsService } from '@services/settings/settings.service';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { GlobalParamsService } from '../global-params/global-params.service';
import { PermissionsService } from './../auth/permissions.service';

import { v4 as uuidv4 } from 'uuid';
import { ApplicationStatus } from './application-status';

type DBStatus = 'fetching' | 'fetched' | 'running' | 'completed';

@Injectable({ providedIn: 'root' })
export class ResultsStoreService {
  public calculationResult$ = new ReplaySubject<ResultItem>();
  public noRedrawResults = false;
  public lastClosedResultset: ResultsetItem = null;
  public ellipsisMapping = new Map();
  public displayZeroAsMapping = new Map();
  public importSettings$ = new BehaviorSubject(null);
  public numPeriodsOfRanSimulations$ = new BehaviorSubject(null);
  public firstResultsFetchIsPending = true;
  public currentOrdering = {};

  private _resultsList$: BehaviorSubject<ResultsetItem[]>;
  private _datasetMap: Map<number, DatasetDetails>;
  public get datasetMap(): Map<number, DatasetDetails> {
    return this._datasetMap;
  }
  public set datasetMap(value: Map<number, DatasetDetails>) {
    this._datasetMap = value;
  }
  private _scenarioMap: Map<number, Scenario>;
  public get scenarioMap(): Map<number, Scenario> {
    return this._scenarioMap;
  }
  public set scenarioMap(value: Map<number, Scenario>) {
    this._scenarioMap = value;
  }

  private _DBstate: DBStatus;
  private _appStatus$: BehaviorSubject<ApplicationStatus>;

  public get DBstate(): DBStatus {
    return this._DBstate;
  }
  public set DBstate(value: DBStatus) {
    this._DBstate = value;
  }

  public get appStatus$(): BehaviorSubject<ApplicationStatus> {
    return this._appStatus$;
  }
  public set appStatus$(value: BehaviorSubject<ApplicationStatus>) {
    this._appStatus$ = value;
  }

  public get resultsList(): ResultsetItem[] {
    return this.resultsList$.getValue();
  }
  public set resultsList(value: ResultsetItem[]) {
    this.resultsList$.next(value);
  }
  public get resultsList$(): BehaviorSubject<ResultsetItem[]> {
    return this._resultsList$;
  }
  public set resultsList$(value: BehaviorSubject<ResultsetItem[]>) {
    this._resultsList$ = value;
  }

  public singleResultQueue$ = new ReplaySubject<ResultsetItem>();
  public isFetchingSingleResultset$ = new BehaviorSubject<boolean>(false);

  constructor(
    private scService: ScenarioService,
    private dsService: DatasetsService,
    private resultsService: ResultsService,
    private notifications: NotificationsService,
    private permissions: PermissionsService,
    private globalParams: GlobalParamsService,
    private settingsService: SettingsService
  ) {
    this.resultsList$ = new BehaviorSubject<ResultsetItem[]>([]);
    this.resultsList = [];
    this.appStatus$ = new BehaviorSubject<ApplicationStatus>(ApplicationStatus.Idle);

    this.appStatus$.subscribe((status: ApplicationStatus) => {
      switch (status) {
        case ApplicationStatus.DoneSimulation:
          this.currentOrdering = {};
          for (const result of this.resultsList) {
            this.addToOrderingTable(result);
          }
      }
    });

    this.scService.scenarioAction$.subscribe(action => {
      const list = this.scService.scenarioList$.getValue();
      const actionsToIgnore = [ScenarioAction.Idle, ScenarioAction.Created, ScenarioAction.Fetch];
      if (!actionsToIgnore.includes(action)) {
        this.loadDataFromDB(true, false, false);
        this.appStatus$.next(ApplicationStatus.ScenariosModified);
      }
      this.scenarioMap = this.transformScenarioListIntoMap(list);
    });
  }

  /**
   * Loads the results store with the resultset api
   * @param forceFetchResults Optional. Allows force reload of resultsets
   * @param fetchScenarios Optional. Allows bypass a refetch of scenarios
   * @param fetchDatasets Optional. Allows bypass a refetch of datasets
   */
  public async loadDataFromDB(forceFetchResults = false, fetchScenarios = true, fetchDatasets = true) {
    if (fetchDatasets) this.datasetMap = await this.getDataSetsFromDB();
    if (fetchScenarios) await this.getScenariosFromDB();

    if (this.resultsList.length <= 0 || !this.resultsList || forceFetchResults) {
      this.appStatus$.next(ApplicationStatus.FetchingResults);
      this.currentOrdering = {};

      const resultApiResult = await this.resultsService.getSummaryFromDB();
      if (resultApiResult) {
        // Period 0 Date from Global Params
        try {
          this.resultsService.setAsOfDate(resultApiResult[0].Results.PeriodDates[0]);
          // console.log('PeriodDates --> asOfDate is', this.resultsService.asOfDate);
        } catch (err) {
          if (JSON.stringify(resultApiResult) !== '[]') {
            console.warn('Unable to retrieve a valid asOfDate (Period 0 date)');
          }
        }
        const mappedSets = this.mapResultSets(resultApiResult, this.datasetMap, this.scenarioMap);
        this.resultsList = mappedSets;
        this.settingsService.setResultsColors(mappedSets);

        this.appStatus$.next(ApplicationStatus.DoneFetchingResults);
        this.checkIfResultsOutOfDate();

        // Emit the number of periods of every result (on load) for Fix 4795
        try {
          if (resultApiResult && resultApiResult.length > 0) {
            const ranNumPeriodsList = resultApiResult.map(result => result.Results.PeriodDates.length - 1);
            if (ranNumPeriodsList) this.numPeriodsOfRanSimulations$.next(ranNumPeriodsList);
          }
        } catch (err) {
          console.warn('Error retrieving the number of periods for every result while fetching results');
        }

        if (this.firstResultsFetchIsPending) {
          // Fix 4775: We only want to trigger It once the results are loaded for first time
          this.checkForImport();
          this.firstResultsFetchIsPending = false;
        }

        return mappedSets;
      }
    }

    return this.resultsList || [];
  }

  public async getSingleResultset(runId) {
    this.isFetchingSingleResultset$.next(true);
    this.appStatus$.next(ApplicationStatus.FetchSingleResult);
    const result = await this.resultsService.getSingleResultSetsFromDB(runId);

    this.updateByUUID(result.RunID, result, result);
    this.singleResultQueue$.next(result);
    this.appStatus$.next(ApplicationStatus.DoneFetchingSingleResult);
    return result;
  }

  public async checkForImport() {
    // Being used for displaying or not the Import Banner Notification
    const res = await this.dsService.GetImportSettings().toPromise();
    return res;
  }

  /**
   * Open or close a resultset from the view and updates the store with the new result
   * @param resultset The resultset to performs actions against
   * @param toHide Boolean that indicates if we're hiding (closing):True or unhiding (opening):False a resultset.
   */
  public hideShowResultSet(resultset: ResultsetItem, toHide: boolean) {
    this.noRedrawResults = true;

    this.resultsService.hideShowResultSet(resultset, toHide).then(([hideShowResponse, updatedResultset]) => {
      if (updatedResultset) {
        this.updateResultsetWithHideValue({ ...updatedResultset, Order: hideShowResponse.ResultSetOrder }, toHide);
      } else {
        this.updateResultsetWithHideValue({ ...resultset, Order: hideShowResponse.ResultSetOrder }, toHide);
      }
      this.appStatus$.next(toHide ? ApplicationStatus.CloseComplete : ApplicationStatus.OpenComplete);
    });

    // For each stress
    if (toHide && resultset.StressList) {
      resultset.StressList.forEach(stress => {
        if (!stress.Hidden) {
          this.resultsService.hideShowResultSet(stress, toHide).then(([hideShowResponse, updatedResultset]) => {
            if (updatedResultset) {
              this.updateResultsetWithHideValue({ ...updatedResultset, Order: hideShowResponse.ResultSetOrder }, toHide);
            } else {
              this.updateResultsetWithHideValue({ ...stress, Order: hideShowResponse.ResultSetOrder }, toHide);
            }
          });
        }
      });
    }
    this.noRedrawResults = false;

    if (toHide) {
      this.lastClosedResultset = resultset;
    } else {
      // Check if the newly open resultsets are out of date
    }

    // this.appStatus$.next(toHide ? ApplicationStatus.CloseComplete : ApplicationStatus.OpenComplete);
  }

  private updateResultsetWithHideValue(resultset: ResultsetItem, toHide: boolean) {
    if (toHide) {
      // Remove resultset from ordering table
      resultset.Order = -1;
      this.removeFromOrderingTable(resultset);
    }

    if (resultset.IsTemp && resultset.ResultSetID > 0 && toHide) {
      // If temp result, there's a previous record and we're hiding
      this.updateByUUID(resultset.RunID, {
        Hidden: toHide,
        TempResultSetID: -1,
        IsTemp: false,
        OpenCloseHappening: false,
      });
    } else if (resultset.IsTemp && toHide) {
      // If temp result, but no previous record and we're hiding
      this.remove(resultset);
    } else {
      // All other cases

      if (!toHide) {
        this.addToOrderingTable({ ...resultset, Hidden: false });
      }

      this.updateByUUID(
        resultset.RunID,
        { ...resultset, Hidden: toHide, OpenCloseHappening: false, Order: resultset.Order, isTabDisabled: false },
        undefined,
        true
      );

      this.calculationResult$.next(resultset as any);
      this.checkIfResultsOutOfDate();
    }
    // this.appStatus$.next(toHide ? ApplicationStatus.CloseComplete : ApplicationStatus.OpenComplete);
  }

  public getBaselineScenariosList(scenarioMap = this.scenarioMap) {
    return scenarioMap ? new Map(Array.from(scenarioMap).filter(([k, v]) => v.is_stress === 0)) : new Map();
  }

  public getStressScenariosList(scenarioMap = this.scenarioMap) {
    return scenarioMap ? new Map(Array.from(scenarioMap).filter(([k, v]) => v.is_stress === 1)) : new Map();
  }

  public getResultTree(resultList = this.resultsList) {
    const baselineScenarios = resultList.filter(x => !x.Is_Stress).sort(this.sortResultsets);
    baselineScenarios.forEach(base => {
      base.StressList = resultList.filter(x => x.RunID_Used === base.RunID).sort(this.sortResultsets);
    });
    return baselineScenarios;
  }

  /**
   * Predicate that sorts resultsets based on their Order property.
   * If the Order property does not exist, it's less than 0 or the resultset is Hidden, it will be placed at the bottom
   */
  private sortResultsets(a: ResultsetItem, b: ResultsetItem) {
    if (!a.Order || a.Order === -1 || a.Hidden) {
      return 1;
    }
    if (!b.Order || b.Order === -1 || b.Hidden) {
      return -1;
    }
    return a.Order - b.Order;
  }

  public async getScenariosFromDB() {
    const scenarioList = await this.scService.getScenarios().toPromise();
    return this.transformScenarioListIntoMap(scenarioList);
  }

  private transformScenarioListIntoMap(scenarioList: Scenario[]): Map<number, Scenario> {
    const hashmap = new Map<number, Scenario>();
    if (scenarioList.length > 0) {
      scenarioList
        .filter(element => element.record_status === 'A')
        .forEach(element => {
          hashmap.set(element.scenario_id, element);
        });
    }
    return hashmap;
  }

  public async getDataSetsFromDB() {
    const datasetList = await this.dsService.getDatasets().toPromise();

    const hashmap = new Map<number, DatasetDetails>();
    if (datasetList) {
      datasetList.forEach(element => hashmap.set(element.DataSetID, element));
    }
    return hashmap;
  }

  public mapResultSets(resultsets, datasets: Map<number, DatasetDetails>, scenarios: Map<number, Scenario>): ResultsetItem[] {
    if (resultsets) {
      const mappedResults = resultsets.map(resultset => {
        const scenarioDetails = resultset.Scenario_ID
          ? scenarios.get(resultset.Scenario_ID)
          : resultset.TheScenario && resultset.TheScenario.Scenario_ID
          ? scenarios.get(resultset.TheScenario.Scenario_ID)
          : null;

        const datasetDetails = datasets.get(resultset.DataSetID);
        const runid = <string>(resultset.RunID || (resultset.Results && resultset.Results.RunID) || uuidv4());

        const runDate = resultset.Run_Date ? new Date(`${resultset.Run_Date} UTC`) : new Date();

        this.addToOrderingTable({ ...resultset, RunID: runid });

        const mappedResult = {
          id: runid,
          // id: this.getID(resultset),
          ResultSetID: resultset.ResultSetID,
          Description: resultset.Description,
          Is_Stress: resultset.Is_Stress,
          DataSetID: resultset.DataSetID,
          DataSetName: datasetDetails ? datasetDetails.DataSetName : `*dataset no longer exists*`,
          ResultSetId_Used: resultset.ResultSetId_Used,
          Scenario_ID: resultset.Scenario_ID,
          Scenario_Mnemonic: scenarioDetails ? scenarioDetails.mnemonic : '',
          IsTemp: resultset.IsTemp,
          Mnemonic: resultset.Mnemonic,
          TempResultSetID: resultset.TempResultSetID,
          Hidden: resultset.Hidden,
          Run_Date: runDate,
          RunID: runid,
          Results: resultset.Results,
          IgnoreErrors: resultset.IgnoreErrors,
          Expanded: true,
          IsLoading: resultset.IsLoading || false,
          InMemory: resultset.InMemory || false,
          Status: [],
          RunID_Used: resultset.RunID_Used,
          Parent: resultset.Parent ? resultset.Parent : null,
          Order: resultset.Order,
          HasNotifications: resultset.HasNotifications,
        };
        return mappedResult;
      });

      return mappedResults;
    } else {
      return [];
    }
  }

  /**
   * Add a new result to the ordering table
   */
  public addToOrderingTable(resultset: ResultsetItem) {
    if (!resultset.Hidden) {
      if (!this.currentOrdering[resultset.Is_Stress ? resultset.RunID_Used : 'root']) {
        this.currentOrdering[resultset.Is_Stress ? resultset.RunID_Used : 'root'] = {};
      }
      this.currentOrdering[resultset.Is_Stress ? resultset.RunID_Used : 'root'][resultset.RunID] = resultset.Order;
    }
  }

  /**
   * Deletes a result from its ordering table. It will also delete the ordering table for its children, if any
   */
  public removeFromOrderingTable(resultset: ResultsetItem) {
    const elementInOrderingTable = this.currentOrdering[resultset.Is_Stress ? resultset.RunID_Used : 'root'];
    if (elementInOrderingTable && elementInOrderingTable[resultset.RunID]) {
      delete elementInOrderingTable[resultset.RunID];
    }

    // If we're deleting a baseline, clear out also the ordering table for their stresses
    if (!resultset.Is_Stress && this.currentOrdering[resultset.RunID_Used]) {
      delete this.currentOrdering[resultset.RunID_Used];
    }
  }

  public getNextOrder(identifiers, parentId, offset = 0) {
    const result = identifiers
      .filter(resultset => resultset.RunID_Used === (parentId === 'root' ? '' : parentId) && resultset.Hidden === false)
      .reduce((max, resultset) => (resultset.Order > max ? resultset.Order : max), -1);
    return result + offset;
  }

  public add(resultsets) {
    const mappedSets = this.mapResultSets([resultsets], this.datasetMap, this.scenarioMap);
    mappedSets.forEach(set => (this.resultsList = [...this.resultsList, set]));
  }

  public updateByUUID(runID: string, newData: ResultsetItem | any, fullobj?: any, ignoreHiddenAction?: boolean) {
    const resultIndex = this.resultsList.findIndex(item => item.RunID === runID);

    // We don't want to run this update if the update comes from a Hide/Unhide action
    if (!ignoreHiddenAction) {
      // Check if we're going from Hide to Unhide
      const hiddenAction =
        newData && newData.hasOwnProperty('Hidden') ? newData.Hidden : fullobj && fullobj.hasOwnProperty('Hidden') ? fullobj.Hidden : true;

      if (resultIndex >= 0 && this.resultsList[resultIndex].Hidden === true && hiddenAction === false) {
        this.resultsService.hideShowResultSet(this.resultsList[resultIndex], false);
      }
    }

    if (resultIndex >= 0) {
      this.resultsList[resultIndex] = { ...this.resultsList[resultIndex], ...newData };
      this.resultsList = [...this.resultsList];
    } else {
      this.add(fullobj);
    }
  }

  public remove(resultset) {
    // Clear the resultset from the ordering table
    this.removeFromOrderingTable(resultset);

    // Now from the Store
    if (resultset.Is_Stress) {
      this.resultsList = [...this.resultsList.filter(resu => resu.RunID !== resultset.RunID)];
    } else {
      // Remove the StressList from the collection
      const childrenIds: any[] = resultset.StressList.map(x => x.RunID);
      const resultsListWithNoStressList = [...this.resultsList.filter(result => !childrenIds.includes(result.RunID))];
      // Remove any resultset that is pointing to this baseline as used
      // note: defensive programming -> this should never have to be done
      const resultsListWithNoReferences = resultsListWithNoStressList.filter(
        result => !(result && result.RunID_Used && result.RunID_Used === resultset.RunID)
      );
      // Finally remove the baseline
      const cleanedResultsList = resultsListWithNoReferences.filter(result => result.RunID !== resultset.RunID);
      this.resultsList = [...cleanedResultsList];
    }

    this.notifications.deleteNotificationsByMnemonic(resultset.Mnemonic);
  }

  public removeResultsetByScenarioID(scenarioID: number) {
    this.resultsList = this.resultsList.filter(result => result.Scenario_ID !== scenarioID);
  }

  /**
   * Runs a check in all resultsets passed to verify if they're out of date and a notification needs to be added
   * @param resultsetList The collection of resultsets that we're validating for dates. If none is specified,
   * will get the complete list from the Store
   */
  public checkIfResultsOutOfDate(resultsetList: ResultsetItem[] = this.resultsList) {
    resultsetList.forEach((resultset: ResultsetItem) => {
      // Skip resultsets that are undefined, null, or have no Run_Date (likely being removed)
      if (!resultset || !resultset.Run_Date) {
        return;
      }

      // Clear previous notifications
      this.notifications.deleteNotificationsByMnemonic(resultset.Mnemonic, 'resultset-ood');

      // Is out of date ?
      const isResultOutOfDate =
        isDatasetNewer(resultset, this.datasetMap) ||
        isBaselineDateNewerThanStress(resultset, this.resultsList) ||
        !isDatasetStillInDB(resultset, this.datasetMap);

      // IF YES, Create a new notification for out of date if it fails the rules
      if (isResultOutOfDate) {
        this.notifications.addNewNotification(
          'resultset-ood',
          'RESULT-SET OUT OF DATE',
          `New data available!${this.permissions.getPermission('RunSimulation') === 'W' ? ' You should rerun the simulation.' : ''}`,
          { mnemonic: resultset.Mnemonic, data: resultset }
        );
      }
    });
  }

  public flagEllipsisForConversionOnExport(
    workSheet: GC.Spread.Sheets.Worksheet,
    cell: GC.Spread.Sheets.CellRange,
    numericValue: number,
    maxWidthEllipsis: number
  ) {
    return this.ellipsisMapping.set(`${workSheet.name()} - ${cell.row} - ${cell.col}`, {
      textValue: cell.text(),
      numericValue: numericValue,
      sheet: workSheet,
      row: cell.row,
      col: cell.col,
      maxWidthEllipsis,
    });
  }

  public flagDisplayZeroAsForConversionOnExport(workSheet: GC.Spread.Sheets.Worksheet, cell: GC.Spread.Sheets.CellRange) {
    const format = cell.formatter();
    if (format) {
      return this.displayZeroAsMapping.set(`${workSheet.name()} - ${cell.row} - ${cell.col}`, {
        format,
        sheet: workSheet,
        row: cell.row,
        col: cell.col,
      });
    }
  }
}
